import { useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore'
import type { QuerySnapshot, DocumentData } from 'firebase/firestore'
import type { DashboardDoc } from '@racedash/shared'
import { db } from '../lib/firebase'

export interface Dashboard extends DashboardDoc {
  id: string
}

/**
 * Lists dashboards in an organisation.
 *
 * This is deliberately TWO queries merged client-side, not one. Firestore
 * evaluates `list` rules against every document a query returns and fails the
 * whole query if any single document fails — so an unfiltered query over the
 * org would hit somebody else's draft and error out entirely, rather than
 * quietly omitting it. Splitting into "org-visible" and "mine" means every
 * document in each result set is provably readable.
 *
 * Owners are permitted by the rules to read other members' drafts, but this
 * does not surface them: seeing colleagues' unfinished work in the normal list
 * would be surprising. That permission exists for recovering a leaver's drafts,
 * which is a deliberate action rather than a default view.
 */
export function useDashboards(orgId: string | undefined, uid: string, trashed = false) {
  const [shared, setShared] = useState<Dashboard[] | null>(null)
  const [mine, setMine] = useState<Dashboard[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId) return
    setShared(null)
    setMine(null)
    setError(null)

    const toList = (snap: QuerySnapshot<DocumentData>): Dashboard[] =>
      snap.docs.map((d) => ({ id: d.id, ...(d.data() as DashboardDoc) }))

    const base = [
      where('orgId', '==', orgId),
      where('deleted', '==', trashed),
      orderBy('updatedAt', 'desc'),
    ]

    const unsubShared = onSnapshot(
      query(collection(db, 'dashboards'), where('visibility', '==', 'organisation'), ...base),
      (snap) => setShared(toList(snap)),
      (e) => setError(e.message),
    )

    const unsubMine = onSnapshot(
      query(collection(db, 'dashboards'), where('createdBy', '==', uid), ...base),
      (snap) => setMine(toList(snap)),
      (e) => setError(e.message),
    )

    return () => {
      unsubShared()
      unsubMine()
    }
  }, [orgId, uid, trashed])

  // The two result sets overlap — anything org-visible that you created appears
  // in both — so dedupe by id, then re-sort, since merging loses the ordering
  // each query arrived in.
  const dashboards = useMemo(() => {
    if (shared === null || mine === null) return null
    const byId = new Map<string, Dashboard>()
    for (const d of [...shared, ...mine]) byId.set(d.id, d)
    return [...byId.values()].sort(
      (a, b) => (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0),
    )
  }, [shared, mine])

  return { dashboards, error }
}
